import Box from '@mui/material/Box';
import { IconButton, colors } from '@sweep-io/sweep-design';
import { useEffect } from 'react';
import { buildConfigurationItemId, HEADER_TOP_ENDORNMENT } from './helpers';
import { ConfigurationType } from './types';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { useDependencies } from './hooks/useDependencies';
import DependenciesPaywallBlock from './DependenciesPaywallBlock';
import { isCustomObjectByName } from '../cpq/utils';
import { UI_EVENTS } from '../../../services/events';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { DependenciesContentWrapper } from './DependenciesContentWrapper';
import usePaywall from '../../dashboard/paywall/usePaywall';
import { useTags } from './annotations/tags/useTags';
import { ConfigurationItem } from '../../parser/ParserTypes';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import { useSetQueryParamsOnMount } from '../../../navigation-utils/useSetQueryParamsOnMount';
import { getConfigurationItemColor } from '../helpers';
import { useSelector } from 'react-redux';
import { selectTagsList } from '../../../reducers/documentation/userInputsReducer';
import { useLocation } from 'react-router';
import { appRoutes } from '../../../constants/appRoutes';

interface DependenciesSideDialogContentProps {
  crmOrgId: string;
  configurationItem: ConfigurationItem;
  type: ConfigurationType;
  onClose?: () => void;
  isReadOnly?: boolean;
  headerContent?: React.ReactNode;
  color?: string;
}

export const DependenciesSideDialogContent = ({
  crmOrgId,
  configurationItem,
  type,
  onClose,
  isReadOnly = false,
  headerContent,
  color,
}: DependenciesSideDialogContentProps) => {
  const { isPaywallActive } = usePaywall();
  const { objectApiNames } = configurationItem ?? {};
  const { clearTagOrUserConfigurations } = useTags(crmOrgId);
  const { handleCloseDependencies } = useDependencies(crmOrgId);
  const _objectName = objectApiNames?.[0];
  const isPaywall = isPaywallActive && _objectName && isCustomObjectByName(_objectName);
  const tags = useSelector(selectTagsList);
  const { pathname } = useLocation();

  const isOrgMigration = pathname.startsWith(appRoutes.orgMigration.route);
  const bypassEntirely = !configurationItem.parentType || isReadOnly || isOrgMigration;

  useSetQueryParamsOnMount(
    {
      [DeepLinkSearchParams.configurationItemId]: buildConfigurationItemId({
        parentType: configurationItem.parentType || '',
        name: configurationItem.name,
        id: configurationItem.id,
      }),
    },
    bypassEntirely,
  );

  const sendBiEvent = useSendBiEvent();
  useEffect(() => {
    const props = {
      element: type,
      object: _objectName,
    };
    sendBiEvent({ name: UI_EVENTS.documentationOpenCard, props });
  }, [sendBiEvent, _objectName, type]);

  useEffect(() => {
    //clear tags after closing side dialog
    return () => {
      clearTagOrUserConfigurations();
    };
  }, [clearTagOrUserConfigurations]);

  const handleClose = onClose || handleCloseDependencies;

  return (
    <>
      <Box
        sx={{
          background: getConfigurationItemColor(configurationItem, color, tags),
          width: '100%',
          height: HEADER_TOP_ENDORNMENT,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          pr: 1,
          gap: 1,

          //TODO add option to design system
          button: {
            '&:hover': {
              backgroundColor: 'transparent',
              overflow: 'hidden',

              '&:before': {
                content: '""',
                position: 'absolute',
                height: '100%',
                width: '100%',
                backgroundColor: colors.grey[120],
                mixBlendMode: 'soft-light',
                zIndex: '1',
              },
            },
          },
        }}
        data-testid="dependencies-side-dialog-close-button"
      >
        {!isPaywall && headerContent}
        <IconButton variant="flat" size="tiny" onClick={handleClose}>
          <Close />
        </IconButton>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        height={`calc(100% - ${HEADER_TOP_ENDORNMENT})`}
        width="100%"
        overflow="scroll"
      >
        {isPaywall && <DependenciesPaywallBlock objectName={_objectName} />}
        {!isPaywall && (
          <DependenciesContentWrapper
            crmOrgId={crmOrgId}
            configurationItem={configurationItem}
            type={type}
            isReadOnly={isReadOnly}
          />
        )}
      </Box>
    </>
  );
};
